import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';

export type TruncatingTypographyProps = TypographyProps & {
  tooltipPlacement?: TooltipProps['placement'];
  tooltipEnterDelay?: number;
  tooltipTitle?: string;
};

function tooltipTitleFor(children: ReactNode, customTooltipTitle?: string): string | undefined {
  if (customTooltipTitle != null) return customTooltipTitle;
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  return '';
}

function TruncatingTypography(props: TruncatingTypographyProps) {
  const {
    children,
    noWrap = true,
    tooltipPlacement = 'top',
    tooltipEnterDelay = 400,
    tooltipTitle,
    sx,
    ...typographyProps
  } = props;

  const elRef = useRef<HTMLElement | null>(null);
  const [isClipped, setIsClipped] = useState(false);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = elRef.current;
    if (!noWrap || !el) {
      setIsClipped(false);
      return;
    }

    const update = () => setIsClipped(el.scrollWidth > el.clientWidth + 1);
    update();

    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [noWrap, children]);

  useEffect(() => {
    if (!open) return;

    const handleScroll = () => setOpen(false);
    // Capture phase so nested scroll containers (e.g. filter menus) are caught
    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, [open]);

  if (!noWrap) {
    return (
      <Typography noWrap={false} sx={sx} {...typographyProps}>
        {children}
      </Typography>
    );
  }

  const title = tooltipTitleFor(children, tooltipTitle);
  const canShowTooltip =
    isClipped && title != null && (typeof title !== 'string' || title.length > 0);

  return (
    <Tooltip
      title={title ?? ''}
      placement={tooltipPlacement}
      enterDelay={tooltipEnterDelay}
      open={canShowTooltip && open}
      onOpen={() => {
        if (canShowTooltip) setOpen(true);
      }}
      onClose={() => setOpen(false)}
      disableHoverListener={!canShowTooltip}
      componentsProps={{
        tooltip: {
          sx: {
            borderRadius: '4px',
            bgcolor: '#162C36',
            width: 'fit-content',
            padding: '0.25rem',
            position: 'relative',
            color: '#F6F8F9',
            fontSize: '0.75rem',
            fontWeight: 500,
            lineHeight: '1rem',
          },
        },
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: ({ reference }: { reference: { height: number } }) => [
                  0,
                  -reference.height * 0.4,
                ],
              },
            },
          ],
        },
      }}
    >
      <Typography
        ref={elRef}
        noWrap
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: isClipped ? 'pointer' : 'inherit',
          ...sx,
        }}
        {...typographyProps}
      >
        {children}
      </Typography>
    </Tooltip>
  );
}

export default TruncatingTypography;
